// Reusable weekly access-span table — extracted from AccessSpansEditor
// (Phase 2 Task 9) so the same add/remove/edit-day+time-window UI and
// minute<->row conversion helpers can be shared with the global Schedule
// span editor (see src/pages/SchedulesPage.tsx, Task 7). This module owns
// only the controlled `<Table>` of rows; callers own the page/section
// chrome around it (title, "add row" button, loading/error/empty states,
// save button) since those differ per consumer.
import { Button, Input, Table } from '@cloudflare/kumo'
import { TrashIcon } from '@phosphor-icons/react'
import { useTranslation } from '../../i18n'

export const DAY_FIELDS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
export type DayField = (typeof DAY_FIELDS)[number]

export interface SpanRow extends Record<DayField, boolean> {
  startTime: string
  endTime: string
}

/** Shape shared by both CompanyAccessSpan and Schedule span DTOs — minute offsets + a day-of-week bitmap. Extra fields (id, scheduleId/companyId, createdAt) are fine since callers pass a superset. */
export interface SpanDayDto extends Record<DayField, boolean> {
  startMinute: number
  endMinute: number
}

export function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0')
  const m = (minutes % 60).toString().padStart(2, '0')
  return `${h}:${m}`
}

export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map((part) => Number(part))
  return (h || 0) * 60 + (m || 0)
}

export function spanDtoToRow<T extends SpanDayDto>(dto: T): SpanRow {
  return {
    startTime: minutesToTime(dto.startMinute),
    endTime: minutesToTime(dto.endMinute),
    sun: dto.sun,
    mon: dto.mon,
    tue: dto.tue,
    wed: dto.wed,
    thu: dto.thu,
    fri: dto.fri,
    sat: dto.sat,
  }
}

export function spanRowToDto(row: SpanRow): SpanDayDto {
  return {
    startMinute: timeToMinutes(row.startTime),
    endMinute: timeToMinutes(row.endTime),
    sun: row.sun,
    mon: row.mon,
    tue: row.tue,
    wed: row.wed,
    thu: row.thu,
    fri: row.fri,
    sat: row.sat,
  }
}

export function emptySpanRow(): SpanRow {
  return {
    startTime: '09:00',
    endTime: '18:00',
    sun: false,
    mon: false,
    tue: false,
    wed: false,
    thu: false,
    fri: false,
    sat: false,
  }
}

/** Returns an i18n key describing the first validation failure, or null if every row is valid. */
export function validateSpanRows(rows: SpanRow[], t: (key: string) => string): string | null {
  for (const row of rows) {
    if (timeToMinutes(row.startTime) >= timeToMinutes(row.endTime)) {
      return t('accessSpans.validation.timeOrder')
    }
    if (!DAY_FIELDS.some((day) => row[day])) {
      return t('accessSpans.validation.dayRequired')
    }
  }
  return null
}

export interface SpanTableEditorProps {
  rows: SpanRow[]
  onRemoveRow: (index: number) => void
  onUpdateRow: (index: number, patch: Partial<SpanRow>) => void
  onToggleDay: (index: number, day: DayField) => void
}

export function SpanTableEditor({
  rows,
  onRemoveRow,
  onUpdateRow,
  onToggleDay,
}: SpanTableEditorProps) {
  const { t } = useTranslation()

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.Head>{t('accessSpans.days')}</Table.Head>
            <Table.Head>{t('accessSpans.startTime')}</Table.Head>
            <Table.Head>{t('accessSpans.endTime')}</Table.Head>
            <Table.Head>{t('accessSpans.actions')}</Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row, index) => (
            <Table.Row key={index} data-testid={`access-span-row-${index}`}>
              <Table.Cell>
                <div className="flex gap-2">
                  {DAY_FIELDS.map((day) => (
                    <label
                      key={day}
                      className="flex flex-col items-center gap-1 text-xs text-kumo-subtle"
                    >
                      <input
                        type="checkbox"
                        aria-label={t(`accessSpans.day.${day}`)}
                        checked={row[day]}
                        onChange={() => onToggleDay(index, day)}
                      />
                      {t(`accessSpans.day.${day}`)}
                    </label>
                  ))}
                </div>
              </Table.Cell>
              <Table.Cell>
                <Input
                  type="time"
                  aria-label={t('accessSpans.startTime')}
                  value={row.startTime}
                  onChange={(e) => onUpdateRow(index, { startTime: e.target.value })}
                />
              </Table.Cell>
              <Table.Cell>
                <Input
                  type="time"
                  aria-label={t('accessSpans.endTime')}
                  value={row.endTime}
                  onChange={(e) => onUpdateRow(index, { endTime: e.target.value })}
                />
              </Table.Cell>
              <Table.Cell>
                <Button
                  variant="secondary-destructive"
                  size="sm"
                  icon={<TrashIcon size={16} />}
                  aria-label={t('accessSpans.removeRow')}
                  onClick={() => onRemoveRow(index)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  )
}

SpanTableEditor.displayName = 'SpanTableEditor'
